'use client'
import React from 'react';
import { type Blog } from '@/lib/@types/api/blog';
import { Link } from "@/navigation";
import moment from 'moment';
import CustomImage from '../Image';
import MyEmpty from '../MyEmpty';
import { useTranslations } from 'next-intl';

interface BlogListFeaturedProps {
	blogList: Blog.BlogListItem[];
}

const BlogListFeatured: React.FC<BlogListFeaturedProps> = ({ blogList }) => {
	const t = useTranslations();
	
	return blogList.length > 0 ? (
		<div className="space-y-8 md:space-y-12">
			{blogList.map((item, index) => {
				const dateStr = item.blog_upload_time ? moment(item.blog_upload_time).format("MM/DD YYYY") : '';
				const [datePart, yearPart] = dateStr.split(' ');
				
				return (
					<div
						key={index}
						className="group relative flex flex-col lg:flex-row gap-6 lg:gap-8 transition-all duration-500 p-5 border-b border-gray-200 hover:border-transparent hover:shadow-[0_4px_12px_rgba(0,0,0,0.1)]"
					>
						{/* 左侧内容 */}
						<div className="flex-1 flex flex-col justify-center space-y-4 lg:space-y-6 order-2 lg:order-1">
							{/* 日期 */}
							<Link 
								href={`/blog/${item.blog_slug}`}
								className="inline-block"
							>
								<div className="text-xl md:text-2xl font-bold mb-2">
									<span className="text-mainColor text-5xl max-md:text-3xl group-hover:text-gray-900 transition-colors duration-300">{datePart}</span>
									<span className="text-gray-400 ml-2">{yearPart}</span>
								</div>
							</Link>
							
							{/* 标题 */}
							<Link 
								href={`/blog/${item.blog_slug}`}
								className="transition-colors duration-300 "
							>
								<h3 className="text-2xl md:text-3xl font-bold text-gray-900 line-clamp-3 mb-4 group-hover:text-mainColor ">
									{item.blog_title}
								</h3>
							</Link>
							
							{/* 摘要 */}
							{item.blog_excerpt && (
								<p className="text-base md:text-lg text-gray-600 leading-relaxed line-clamp-4">
									{item.blog_excerpt}
								</p>
							)}
						</div>
						
						{/* 右侧图片 */}
						<div className="w-full lg:w-1/2 xl:w-2/5 flex-shrink-0 order-1 lg:order-2">
							<Link 
								href={`/blog/${item.blog_slug}`}
								className="block w-full h-full relative overflow-hidden"
							>
								<div className="relative w-full h-[250px] md:h-[350px] xl:h-[430px] overflow-hidden bg-gray-200">
									<CustomImage
										src={item.blog_cover_origin}
										alt={item.blog_title}
										fill={true}
										className="w-full h-full object-cover transition-transform duration-700 ease-in-out transform scale-100 group-hover:scale-105"
										sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
									/>
									{/* 蓝色遮罩 - hover时显示 */}
									<div className="absolute inset-0 bg-mainColor/0 group-hover:bg-mainColor/15 transition-opacity duration-700 ease-in-out"></div>
								</div>
							</Link>
						</div>
						
						{/* 底部从左到右的下划线动画 */}
						<div className="absolute bottom-0 left-0 w-0 h-[3px] bg-mainColor transition-all duration-500 ease-out group-hover:w-full z-10" />
					</div>
				);
			})}
		</div>
	) : (
		<div className="w-full h-full flex justify-center items-center">
			<MyEmpty 
				link={'/blog'} 
				text={''} 
				description={t('nav.Noblog')} 
				bottomText={t('nav.Return_to_blog')}  
				className="py-20 max-md:py-4"
			>
				<i className="ri-draft-line text-4xl !text-ddd"></i>
			</MyEmpty>
		</div>
	);
};

export default BlogListFeatured;

